/*
 * The outbox's replay, with no browser APIs, so server/test-pwa.js runs it in Node against a real server.
 *
 * An item is one thing done while offline: an expense (with an optional receipt photo), a payment on an invoice, a draft
 * line-item invoice or estimate, a note on a customer or vendor, or a timesheet entry. Its id is its Idempotency-Key, made
 * when it was queued, so sending it again after an answer was lost does nothing twice (the server keeps the first answer
 * for 7 days, or CRM_IDEMPOTENCY_DAYS: server/public-api.js staffIdempotency). Items go oldest first, one at a time.
 * Each item also names the organization it was made in (server/orgs.js), and is sent there with X-Org-Id, whichever
 * organization the screen is in when it replays: an invoice id or expense account belongs to one organization's books.
 *
 *   sent                 removed (an expense with a photo first moves to stage "receipt" and sends the photo)
 *   no connection, 401,  kept as it is and the replay stops; it starts again when the server answers or on next sign-in
 *   409 still running,
 *   5xx (under 5 tries)
 *   any other refusal    a conflict: kept, with the server's message, until the person retries or discards it
 *                        (a payment larger than what is now due, an invoice voided meanwhile, a closed period...)
 *   an organization the  not sent at all (`member` says no): kept as it is until the person exports or discards it
 *   person has left
 *
 * Wave 7 (docs/implemented-offline7.md) adds three kinds that change what is already on the server:
 *   edit       a PATCH of a customer, vendor, item, draft invoice, estimate, expense or time entry, sent with the version the
 *              device edited (X-Base-Version). If the server copy changed since, nothing is written: the 409 carries the
 *              server's copy and version, kept on the item (`conflict`) so the person can keep theirs, keep the server's, or
 *              merge field by field (edit-core.ts resolveEdit, which makes a new item with a new key).
 *   issue      an invoice issued with a number reserved to this device (server/offline-sync.js); the server works the totals
 *              out again, and when they differ from what the device showed the item stays as "flagged" until it is read.
 *   approval   an approval decided offline, sent with the version of the request (or of each time entry) the person saw;
 *              changed or already decided since, it is a conflict and nothing is applied.
 * An item that changes a record (`record`) waits while an earlier item for the same record is unresolved, and when an earlier
 * one goes through, the later ones build on the version it wrote, so two offline edits of one record do not conflict with
 * each other.
 *
 * Wave 8 (docs/implemented-offline8.md):
 *   issue      refused by the server for any reason but a deleted draft: the answer says the number is "used on device,
 *              refused" (`offline.refused`); the item keeps that (`refused`) so the outbox can say what to do about an
 *              invoice that was printed with it. It is not sent again.
 *   refuse     a number this device put on an invoice that will never be sent (the person discarded the issue): the server
 *              marks it used on device, refused, so it never goes to another invoice.
 *   payment    on an onboarding instalment too (a Billing invoice), with the amount the device showed as due; changed or paid
 *              meanwhile, it is a conflict with the onboarding as it is now.
 *
 * Wave 9 (docs/implemented-offline9.md):
 *   issue      refused for a reason the device can fix (a missing field, a validation error): the answer says so
 *              (`offline.fixable`) and the number stays reserved to this device until the block's end. The item keeps that
 *              (`fixable`); the person fixes the invoice and sends it again with the SAME number (outbox.ts resendFixed).
 *   edit       of any record or setting (leads, bills, settings…), PUT as well as PATCH, with the version the screen saw.
 *   customer, vendor, item   new ones made with no connection (the full forms), sent once like an expense.
 */

export type Receipt = { name: string; type: string; blob: Blob };

/** What can wait in the outbox, each a POST the server records once per Idempotency-Key. */
export type OutboxKind = "expense" | "payment" | "invoice" | "estimate" | "note" | "time" | "edit" | "issue" | "approval" | "refuse" | "customer" | "vendor" | "item";

/** The server's copy when an edit or approval made offline met a change (409 from server/offline-sync.js). */
export type Conflict = { version: string | null; current: unknown };
/** Wave 8: an invoice issued offline that the server refused; its number is spent (never reused). */
export type Refused = { number: string; reason: string };
/** Wave 9: an invoice issued offline that the server refused for a reason the device can fix; the number waits until `until`. */
export type Fixable = { number: string; reason: string; until: string | null };
/** An invoice issued offline whose figures the server worked out differently. */
export type IssueFlag = { shown_total: string; server_total: string; shown_tax: string | null; server_tax: string };

export type OutboxItem = {
  id: string;
  owner: string;
  /** The organization it was made in, and its name for the outbox list. Items from before organizations: 1. */
  org: number;
  org_name: string | null;
  kind: OutboxKind;
  /** What the person sees: "Expense ₹1,250.00 · Fuel", "Payment ₹5,000.00 on INV-000012", "Draft invoice · Ananya". */
  label: string;
  path: string;
  body: unknown;
  receipt: Receipt | null;
  stage: "record" | "receipt";
  /** The expense made, once stage is "receipt". */
  record_id: string | null;
  state: "pending" | "conflict" | "flagged";
  error: string | null;
  attempts: number;
  created_at: string;
  /** Wave 7, absent on older items: PATCH for an edit (POST otherwise). Wave 9: PUT for a setting. */
  method?: "POST" | "PATCH" | "PUT";
  /** The version the device saw, sent as X-Base-Version. */
  version?: string | null;
  /** The record it changes (its path), so later items for it wait and build on it. */
  record?: string | null;
  /** The values the device started from, for the fields of `body` (an edit). */
  base?: Record<string, unknown> | null;
  conflict?: Conflict | null;
  flag?: IssueFlag | null;
  /** Wave 8: the server refused this issue and marked its number used on the device, refused. */
  refused?: Refused | null;
  /** Wave 9: refused for a reason this device can fix; sent again with the same number until the block's end. */
  fixable?: Fixable | null;
};

export type OutboxStore = {
  list: () => Promise<OutboxItem[]>;
  put: (item: OutboxItem) => Promise<void>;
  remove: (id: string) => Promise<void>;
};

/** Errors carry the HTTP status (0: not reached), as ApiError in src/api/client.ts does. */
export type Failure = { status: number; message: string; data?: unknown };

export type SendOptions = { method?: "POST" | "PATCH" | "PUT"; version?: string | null };
export type Sent = { id?: string; updated_at?: string; offline?: { flag?: IssueFlag | null } } | undefined;

export type OutboxSender = {
  send: (path: string, body: unknown, key: string, org: number, opts?: SendOptions) => Promise<Sent>;
  upload: (recordId: string, receipt: Receipt, key: string, org: number) => Promise<void>;
};

/** `skipped` (only when some were): items of organizations the person has left, not sent. */
export type ReplayResult = { sent: number; conflicts: number; waiting: boolean; skipped?: number; held?: number; flagged?: number };

const MAX_SERVER_ERRORS = 5;

function failure(e: unknown): Failure {
  const x = e as { status?: unknown; message?: unknown; data?: unknown };
  return { status: typeof x?.status === "number" ? x.status : 0, message: typeof x?.message === "string" ? x.message : String(e), data: x?.data };
}

/** The server's copy in a 409 from server/offline-sync.js, if that is what the refusal was. */
export function conflictOf(f: Failure): Conflict | null {
  const c = (f.data as { conflict?: unknown } | undefined)?.conflict as Conflict | undefined;
  return f.status === 409 && c && typeof c === "object" && "current" in c ? { version: typeof c.version === "string" ? c.version : null, current: c.current } : null;
}

/** Wave 8: the server refused an offline issue and spent its number (server/offline-sync.js refusedAnswer). */
function refusedOf(f: Failure): Refused | null {
  const o = (f.data as { offline?: { number?: unknown; refused?: unknown; reason?: unknown } } | undefined)?.offline;
  return o && o.refused === true && typeof o.number === "string" ? { number: o.number, reason: typeof o.reason === "string" ? o.reason : f.message } : null;
}

/** Wave 9: the server refused an offline issue for a reason the device can fix (server/offline-sync.js fixableAnswer). */
function fixableOf(f: Failure): Fixable | null {
  const o = (f.data as { offline?: { number?: unknown; fixable?: unknown; reason?: unknown; until?: unknown } } | undefined)?.offline;
  return o && o.fixable === true && typeof o.number === "string" ? { number: o.number, reason: typeof o.reason === "string" ? o.reason : f.message, until: typeof o.until === "string" ? o.until : null } : null;
}

/** Wait (try later) or give up (a conflict the person must look at)? */
function transient(f: Failure, attempts: number) {
  if (f.status === 0 || f.status === 401 || f.status === 408 || f.status === 429) return true;
  if (f.status === 409 && /still being processed/i.test(f.message)) return true;
  return f.status >= 500 && attempts < MAX_SERVER_ERRORS;
}

type Extra = Partial<Pick<OutboxItem, "method" | "version" | "record" | "base">>;

export function newItem(fields: Pick<OutboxItem, "owner" | "kind" | "label" | "path" | "body"> & { org?: number | null; org_name?: string | null; receipt?: Receipt | null } & Extra, id: string, now = new Date()): OutboxItem {
  return { ...fields, id, org: fields.org ?? 1, org_name: fields.org_name ?? null, receipt: fields.receipt ?? null, stage: "record", record_id: null, state: "pending", error: null, attempts: 0, created_at: now.toISOString() };
}

/**
 * Sends this person's waiting items. `member` (the organizations the person belongs to now) leaves the items of any other
 * organization alone: they are not sent, so they can still be exported or discarded.
 */
export async function replay(store: OutboxStore, sender: OutboxSender, owner: string, member: (org: number) => boolean = () => true): Promise<ReplayResult> {
  const out: ReplayResult = { sent: 0, conflicts: 0, waiting: false };
  const all = (await store.list()).filter((i) => i.owner === owner);
  const items = all.filter((i) => i.state === "pending").sort((a, b) => a.created_at.localeCompare(b.created_at));
  // A record with an unresolved conflict: what comes after it for the same record waits for the person's choice.
  // Wave 9: an issue refused for a reason the device can fix does not: the fix (an edit of the draft) goes before it is sent again.
  const blocked = new Set(all.filter((i) => i.state === "conflict" && i.record && !i.fixable).map((i) => `${i.org ?? 1}:${i.record}`));
  for (let n = 0; n < items.length; n++) {
    let it = items[n];
    const org = it.org ?? 1;
    if (!member(org)) {
      out.skipped = (out.skipped ?? 0) + 1;
      continue;
    }
    const rec = it.record ? `${org}:${it.record}` : null;
    if (rec && blocked.has(rec)) {
      out.held = (out.held ?? 0) + 1;
      continue;
    }
    try {
      if (it.stage === "record") {
        const made = await sender.send(it.path, it.body, it.id, org, it.method || it.version ? { method: it.method, version: it.version ?? null } : undefined);
        // Later items for the same record build on the version this one wrote.
        if (rec && typeof made?.updated_at === "string") {
          for (let m = n + 1; m < items.length; m++) {
            const later = items[m];
            if (later.record && `${later.org ?? 1}:${later.record}` === rec && later.version) {
              items[m] = { ...later, version: made.updated_at };
              await store.put(items[m]);
            }
          }
        }
        const flag = made?.offline?.flag ?? null;
        if (flag) {
          await store.put({ ...it, state: "flagged", flag, error: null });
          out.sent += 1;
          out.flagged = (out.flagged ?? 0) + 1;
          continue;
        }
        if (it.receipt) {
          it = { ...it, stage: "receipt", record_id: made?.id ?? null, attempts: 0 };
          await store.put(it);
        }
      }
      if (it.stage === "receipt" && it.receipt) {
        if (!it.record_id) throw { status: 400, message: "The expense was recorded, but the server did not say which one, so the receipt could not be attached." };
        await sender.upload(it.record_id, it.receipt, `${it.id}:receipt`, org);
      }
      await store.remove(it.id);
      out.sent += 1;
    } catch (e) {
      const f = failure(e);
      const attempts = it.attempts + 1;
      if (transient(f, attempts)) {
        await store.put({ ...it, attempts });
        out.waiting = true;
        break;
      }
      const error = it.stage === "receipt" ? `The expense was recorded, but its receipt could not be attached: ${f.message}` : f.message;
      await store.put({ ...it, attempts, state: "conflict", error, conflict: conflictOf(f), refused: refusedOf(f), fixable: fixableOf(f) });
      if (rec) blocked.add(rec);
      out.conflicts += 1;
    }
  }
  return out;
}

/** Retry a conflict: back in line, its key unchanged (a payment that did go through is not paid twice). */
export const retryItem = (item: OutboxItem): OutboxItem => ({ ...item, state: "pending", error: null, attempts: 0, conflict: null, refused: null, fixable: null });

/**
 * An item as a file the person can keep (an organization they left, or before discarding): what it was, where it was
 * going and what it held. The receipt photo is added by the caller (outbox.ts) as a data URL.
 */
export function exportShape(item: OutboxItem, receipt: string | null = null) {
  const { id, kind, label, org, org_name, path, body, created_at, state, error, stage, record_id } = item;
  return {
    exported_from: "Wealthoak CRM outbox", idempotency_key: id, kind, label, organization: { id: org, name: org_name }, method: item.method ?? "POST", path, body, created_at, state, error, stage, record_id,
    ...(item.version ? { base_version: item.version } : {}),
    ...(item.conflict ? { server_copy: item.conflict.current } : {}),
    receipt: item.receipt ? { name: item.receipt.name, type: item.receipt.type, data_url: receipt } : null,
  };
}
